/* eslint-disable react/jsx-no-useless-fragment */
import { Badge, Button, Form, ScrollArea, Separator, Sheet, Skeleton, Typography } from '@cannect/new-components/atoms'
import { Product } from '@cannect/new-components/molecules'
import { ProductDetailsDialog, Skeletons } from '@cannect/new-components/organisms'
import { BRANDS_TO_REDIRECT_FOR_SALES } from '@cannect/constants/products'
import { RedirectUrls } from '@cannect/constants/texts'
import { AnimatePresence, motion } from 'framer-motion'
import animation from '../../../../constants/animation'
import { useSheetProductsList } from './useSheetProductsList'

import Modal from '@cannect/new-components/Modal'
import { ProductModalDisabled } from '@cannect/new-components/ProductModalDisabled'
import React, { useCallback, useState } from 'react'
import { LuLoader2 } from 'react-icons/lu'

export const SheetProductsListUI = ({
  productsList,
  isLoadingAction,
  isFetchingNextPage,
  onAddToCart,
  currentStepConfig,
  children,
  lastItemRef,
  goToStep,
  queryParams,
  handleFilterChange,
  isInitialProductsLoading
}: ReturnType<typeof useSheetProductsList>) => {
  const [topicProductWarningProductId, setTopicProductWarningProductId] = useState<number | null>(null)
  const [expiringBatchWarningProductId, setExpiringBatchWarningProductId] = useState<number | null>(null)

  const TOPIC_PRODUCT_WARNING_ID = 15757
  const TOPIC_PRODUCT_WARNING_MESSAGE =
    'ATENÇÃO: O produto USA Hemp - Full Spectrum - Tópico Miracle Heal é de uso TÓPICO. Em caso de dúvidas, consulte o profissional de saúde que acompanha seu tratamento.'

  const PRODUCTS_WITH_EXPIRING_BATCH = [15908, 15912, 15914, 15909]
  const EXPIRING_BATCH_WARNING_MESSAGE =
    'Atenção, cliente! O lote disponível deste produto possui vencimento em setembro. Ao adicionar ao carrinho, você está de acordo em receber esse lote. Agradecemos pela compreensão!'

  const handleAddToCart = (productId: number) => {
    if (productId === TOPIC_PRODUCT_WARNING_ID) {
      setTopicProductWarningProductId(productId)
      return
    }

    if (PRODUCTS_WITH_EXPIRING_BATCH.includes(productId)) {
      setExpiringBatchWarningProductId(productId)
      return
    }

    onAddToCart(productId)
  }
  return (
    <Sheet.Root modal={false}>
      <Sheet.Trigger asChild>
        <Button full variant="outline_light" className="gap-1 font-normal">
          {children || (
            <>
              <span className="text-lg font-bold">+</span> Adicionar mais produtos
            </>
          )}
        </Button>
      </Sheet.Trigger>
      <Sheet.Content onInteractOutside={(e) => e.preventDefault()} className="pb-[120px]">
        <Sheet.Header className="mt-8">
          <Sheet.Title>{currentStepConfig.title}</Sheet.Title>
          <Sheet.Description>{currentStepConfig.description}</Sheet.Description>
        </Sheet.Header>
        <Separator className="my-4" />
        <ScrollArea className="h-[calc(100%-120px)] pr-2">
          <AnimatePresence mode="wait">
            <motion.div key={currentStepConfig.filterKey || 'filteredProduducts'} {...animation.fadeInUp}>
              {currentStepConfig?.filters && currentStepConfig.filterKey ? (
                <div className="flex flex-col">
                  {currentStepConfig.filters?.length > 0 ? (
                    currentStepConfig.filters.map((filter) => (
                      <React.Fragment key={filter?.value}>
                        <Form.Checkbox
                          inverted
                          justify="between"
                          label={filter?.label}
                          checked={queryParams
                            ?.getAll(currentStepConfig.filterKey!)
                            ?.includes(filter?.value?.toString())}
                          onCheckedChange={() =>
                            handleFilterChange(currentStepConfig.filterKey!, filter?.value?.toString())
                          }
                        />
                        <Separator className="my-4" />
                      </React.Fragment>
                    ))
                  ) : (
                    <div className="flex flex-col items-center gap-4 py-8">
                      {Array.from({ length: 3 }).map((_, index) => (
                        <Skeleton key={index} className="h-12 w-full" />
                      ))}
                      <Typography.Text className="text-center text-neutral-500">Carregando filtros...</Typography.Text>
                      <Button variant="outline" onClick={() => goToStep('filteredProducts')} className="mt-2">
                        Ver todos os produtos
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {isInitialProductsLoading ? (
                    <div className="space-y-4">
                      <Skeletons.SkeletonProductItem />
                      <Skeletons.SkeletonProductItem />
                    </div>
                  ) : (
                    <>
                      {productsList?.length > 0 ? (
                        <div className="space-y-4">
                          {productsList.map((product, index: number) => (
                            <div
                              key={product.id}
                              ref={index === productsList.length - 1 ? lastItemRef : null}
                              className="rounded-lg border border-neutral-100 bg-white p-4 shadow-sm">
                              <div className="flex gap-4">
                                {/* Imagem do produto */}
                                <ProductDetailsDialog productId={product.id}>
                                  <Product.HeaderImage
                                    className="h-20 w-20 flex-shrink-0 cursor-pointer sm:h-24 sm:w-24"
                                    src={product?.media?.filter((value) => value.format === 'IMG')?.[0]?.path}
                                    alt={product?.name}
                                    isNationalProduct={product?.supplier?.id === 18}
                                    product={product}
                                  />
                                </ProductDetailsDialog>

                                {/* Informações do produto */}
                                <div className="flex min-w-0 flex-1 flex-col gap-3">
                                  {/* Nome, volume e marca */}
                                  <div>
                                    <ProductDetailsDialog productId={product.id}>
                                      <h3 className="cursor-pointer text-sm font-semibold leading-tight text-neutral-900 hover:text-primary-700">
                                        {product?.name}
                                      </h3>
                                    </ProductDetailsDialog>
                                    {product?.medicalData?.volume && (
                                      <p className="text-xs text-neutral-600">
                                        {product.medicalData.volume}
                                        {product?.medicalData?.unit_of_measurement?.toLowerCase()}
                                      </p>
                                    )}
                                    {product?.brand?.name && (
                                      <p className="text-xs text-neutral-500">{product.brand.name}</p>
                                    )}
                                  </div>

                                  {/* Badges */}
                                  <div className="flex flex-wrap gap-2">
                                    {product?.class?.name && (
                                      <Badge variant="neutral" className="text-xs">
                                        {product?.class?.name}
                                      </Badge>
                                    )}
                                    {product?.flavor && (
                                      <Badge className="bg-flavors-default-100 text-xs text-flavors-default-900">
                                        {typeof product.flavor === 'string'
                                          ? product.flavor
                                          : product.flavor?.name || 'Natural'}
                                      </Badge>
                                    )}
                                  </div>

                                  {/* Preço e ação */}
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex flex-col">
                                      <Product.Price
                                        currentPrice={product?.real_price ?? undefined}
                                        promotionalPrice={product?.promotional_price}
                                        className="text-left"
                                      />
                                    </div>

                                    {/* Botão de ação */}
                                    <div className="flex-shrink-0">
                                      {BRANDS_TO_REDIRECT_FOR_SALES.includes(product.brand?.id ?? 0) ? (
                                        <Button
                                          className="gap-1 px-3 text-xs"
                                          variant="outline_light"
                                          size="sm"
                                          onClick={(event) => {
                                            event.preventDefault()
                                            window.open(RedirectUrls.WHATSAPP_SALES, '_blank')
                                          }}
                                          isLoading={isLoadingAction(product.id) || false}>
                                          <span className="text-sm font-bold">+</span> Contato
                                        </Button>
                                      ) : (
                                        <>
                                          {product?.flavors && product?.flavors.length > 1 ? (
                                            <Product.FlavorSelect
                                              className="px-3 text-xs"
                                              isDisabled={isLoadingAction(product.id)}
                                              triggerText="+ Escolher sabor"
                                              variant="outline_light"
                                              size="sm"
                                              unstyled={false}
                                              flavorsList={product?.flavors || []}
                                              onChange={handleAddToCart}
                                              value={product?.flavor || 'Natural'}
                                            />
                                          ) : (
                                            <Button
                                              className="gap-1 px-3 text-xs"
                                              variant="outline_light"
                                              size="sm"
                                              onClick={(event) => {
                                                event.preventDefault()
                                                handleAddToCart(product.id)
                                              }}
                                              isLoading={isLoadingAction(product.id) || false}>
                                              <span className="text-sm font-bold">+</span> Adicionar
                                            </Button>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Typography.Heading type="headingSix" className="text-center">
                          Nenhum produto encontrado para pesquisa selecionada.
                        </Typography.Heading>
                      )}
                    </>
                  )}
                </>
              )}
            </motion.div>
          </AnimatePresence>
          {isFetchingNextPage && (
            <div className="flex w-full items-center justify-center">
              <LuLoader2 size={42} className="animate-spin text-primary" />
            </div>
          )}
        </ScrollArea>

        <Sheet.Footer className="h-[120px] items-center justify-end gap-3 px-11 py-2 pb-6">
          {currentStepConfig.previousStep && (
            <Button
              type="button"
              size="lg"
              unstyled
              className="font-medium text-primary-700"
              onClick={() => goToStep(currentStepConfig.previousStep)}>
              Voltar
            </Button>
          )}
          {currentStepConfig.nextStep ? (
            <Button
              type="button"
              size="lg"
              full
              onClick={() => goToStep(currentStepConfig.nextStep)}
              className="font-medium">
              Avançar
            </Button>
          ) : (
            <Sheet.Close asChild>
              <Button type="submit" size="lg" full>
                Concluir
              </Button>
            </Sheet.Close>
          )}
        </Sheet.Footer>
      </Sheet.Content>

      <ProductModalDisabled
        open={topicProductWarningProductId !== null}
        handleCancel={() => setTopicProductWarningProductId(null)}
        message={TOPIC_PRODUCT_WARNING_MESSAGE}
        primaryButtonOnly
        primaryButtonLabel="Continuar"
        onClick={() => {
          if (topicProductWarningProductId !== null) {
            onAddToCart(topicProductWarningProductId)
          }
          setTopicProductWarningProductId(null)
        }}
      />

      <ProductModalDisabled
        open={expiringBatchWarningProductId !== null}
        handleCancel={() => setExpiringBatchWarningProductId(null)}
        message={EXPIRING_BATCH_WARNING_MESSAGE}
        primaryButtonOnly
        primaryButtonLabel="Continuar"
        onClick={() => {
          if (expiringBatchWarningProductId !== null) {
            onAddToCart(expiringBatchWarningProductId)
          }
          setExpiringBatchWarningProductId(null)
        }}
      />
    </Sheet.Root>
  )
}
